import React from 'react';
import intl from 'react-intl-universal';
import styled from 'styled-components';
import clsx from 'classnames';
import { Classes } from '@blueprintjs/core';
import { Icon } from '@/components/Icon';

const ACCOUNT_TYPE = {
  CASH: 'cash',
  BANK: 'bank',
  CREDIT_CARD: 'credit-card',
};

const ACCOUNT_TYPE_PAIR_ICON = {
  [ACCOUNT_TYPE.CASH]: 'payments',
  [ACCOUNT_TYPE.CREDIT_CARD]: 'credit-card',
  [ACCOUNT_TYPE.BANK]: 'account-balance',
};

// Per-type accent colors for the icon chip.
const ACCOUNT_TYPE_ACCENT = {
  [ACCOUNT_TYPE.CASH]: { bg: '#ecfdf5', fg: '#047857' },
  [ACCOUNT_TYPE.BANK]: { bg: '#eef2ff', fg: '#4f46e5' },
  [ACCOUNT_TYPE.CREDIT_CARD]: { bg: '#fef3e2', fg: '#b45309' },
};

const DEFAULT_ACCENT = { bg: '#f1f1ef', fg: '#6b7280' };

function BankAccountMetaLine({ title, value, className }: any) {
  return (
    <MetaLineWrap className={className}>
      <MetaLineTitle>{title}</MetaLineTitle>
      {value && <MetaLineValue>{value}</MetaLineValue>}
    </MetaLineWrap>
  );
}

function BankAccountBalance({ amount, loading }: any) {
  return (
    <BankAccountBalanceWrap>
      <BankAccountBalanceLabel>{intl.get('balance')}</BankAccountBalanceLabel>
      <BankAccountBalanceAmount
        className={clsx({
          [Classes.SKELETON]: loading,
        })}
      >
        {amount}
      </BankAccountBalanceAmount>
    </BankAccountBalanceWrap>
  );
}

function BankAccountTypeIcon({ type }: any) {
  const icon = ACCOUNT_TYPE_PAIR_ICON[type];
  const accent = ACCOUNT_TYPE_ACCENT[type] || DEFAULT_ACCENT;

  if (!icon) {
    return null;
  }
  return (
    <AccountIconWrap style={{ background: accent.bg, color: accent.fg }}>
      <Icon icon={icon} iconSize={16} />
    </AccountIconWrap>
  );
}

export function BankAccount({
  title,
  code,
  type,
  balance,
  loading = false,
  updatedBeforeText,
  uncategorizedTransactionsCount,
  ...restProps
}: any) {
  return (
    <BankAccountWrap {...restProps}>
      <BankAccountHeader>
        <BankAccountHeaderText>
          <BankAccountTitle className={clsx({ [Classes.SKELETON]: loading })}>
            {title}
          </BankAccountTitle>
          {code && (
            <BankAccountCode className={clsx({ [Classes.SKELETON]: loading })}>
              {code}
            </BankAccountCode>
          )}
        </BankAccountHeaderText>
        {!loading && <BankAccountTypeIcon type={type} />}
      </BankAccountHeader>

      <BankAccountMeta>
        {uncategorizedTransactionsCount > 0 && (
          <BankAccountMetaLine
            title={intl.get('banking.transactions_for_review')}
            value={uncategorizedTransactionsCount}
            className={clsx({ [Classes.SKELETON]: loading })}
          />
        )}
        {updatedBeforeText && (
          <BankAccountMetaLine
            title={updatedBeforeText}
            className={clsx({ [Classes.SKELETON]: loading })}
          />
        )}
      </BankAccountMeta>

      <BankAccountBalance amount={balance} loading={loading} />
    </BankAccountWrap>
  );
}

const BankAccountWrap = styled.div`
  width: 240px;
  min-height: 158px;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  background: #ffffff;
  margin: 8px;
  border: 1px solid #ececec;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
  color: #1f2937;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
  cursor: pointer;

  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 8px 24px rgba(79, 70, 229, 0.08);
    transform: translateY(-1px);
  }
`;

const BankAccountHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 16px 12px;
`;

const BankAccountHeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BankAccountTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: #1f2937;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

const BankAccountCode = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  color: #6b7280;
  background: #f5f5f4;
  border-radius: 999px;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
`;

const BankAccountBalanceWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;
  padding: 12px 16px 14px;
  border-top: 1px solid #f1f1ef;
  background: #fafafa;
  border-radius: 0 0 14px 14px;
  gap: 4px;
`;

const BankAccountBalanceLabel = styled.div`
  text-transform: uppercase;
  font-size: 10.5px;
  letter-spacing: 0.06em;
  color: #6b7280;
  font-weight: 600;
`;

const BankAccountBalanceAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.15;
  color: #111827;
  letter-spacing: -0.01em;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaLineWrap = styled.div`
  font-size: 11.5px;
  display: flex;
  align-items: center;
  color: #6b7280;
  letter-spacing: -0.005em;

  &:not(:first-of-type) {
    margin-top: 4px;
  }
`;

const MetaLineTitle = styled.div`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaLineValue = styled.div`
  box-sizing: border-box;
  background: #eef2ff;
  color: #4f46e5;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  min-width: 22px;
  height: 18px;
  text-align: center;
  font-size: 11px;
  margin-left: 8px;
`;

const BankAccountMeta = styled.div`
  padding: 0 16px 10px;

  &:empty {
    display: none;
  }
`;

export const BankAccountsList = styled.div`
  display: flex;
  margin: -8px;
  flex-wrap: wrap;
`;

const AccountIconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f1f1ef;
  color: #6b7280;
  transition: transform 0.18s ease;

  ${BankAccountWrap}:hover & {
    transform: scale(1.04);
  }
`;
